import { useLocalStorage } from "@mantine/hooks";
import { useMemo } from "react";

import { mergeStoredColumns, sortAndFilterColumns } from "../../utils";

import { APP_VERSION } from "@/config";

import type { LocaleStorageData } from "@/types/storage";
import type { FC } from "react";
import type { ListTableColumnFiltered, ListTableTh } from "../../types";

type ChildrenProps = {
  filteredColumns: ListTableColumnFiltered[];
  headingColumns: ListTableTh[];
  onFilteredColumnsChange: (value: ListTableColumnFiltered[]) => void;
};

export type ListTableContextProps = {
  children?: FC<ChildrenProps>;
  columns?: ListTableTh[];
  disabled?: string[];
  namespace: string;
};

export const ListTableContext: FC<ListTableContextProps> = ({
  children,
  columns = [],
  disabled = [],
  namespace,
}) => {
  const allColumns: ListTableColumnFiltered[] = useMemo(
    () =>
      columns.map(({ id, label }) => ({
        id,
        label,
        active: true,
        // Exclude `make` from being considered disabled by policy
        disabled: id === "make" ? false : disabled.includes(id),
      })),
    [columns, disabled]
  );
  const [{ data, version }, updateStorage] = useLocalStorage<
    LocaleStorageData<ListTableColumnFiltered[]>
  >({
    key: namespace,
    defaultValue: { data: allColumns, version: APP_VERSION },
    getInitialValueInEffect: false,
  });

  const filteredColumns = useMemo(() => {
    const stored = version === APP_VERSION ? data : allColumns;

    return mergeStoredColumns(stored, allColumns).map((col) =>
      col.id === "make" ? { ...col, active: true, disabled: false } : col
    );
  }, [allColumns, data, version]);

  const headingColumns = sortAndFilterColumns(columns, filteredColumns);

  function onFilteredColumnsChange(columns: ListTableColumnFiltered[]) {
    const safeColumns = columns.map((col) =>
      col.id === "make" ? { ...col, active: true, disabled: false } : col
    );

    updateStorage({ data: safeColumns, version: APP_VERSION });
  }

  return children?.({
    filteredColumns,
    headingColumns,
    onFilteredColumnsChange,
  });
};
